import React, { useState, useRef, useEffect } from 'react'
import { Button } from 'react-bootstrap'

const MultiSelectDropdown = ({
  options = [],
  value = [],
  selectedValues = [],
  onChange,
  placeholder = "Select options",
  isSearchable = true,
  isClearable = true,
  className = "",
  style = {},
  disabled = false,
  maxHeight = "200px",
  openUpward = false,
  displayKey = "label",
  valueKey = "value"
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const dropdownRef = useRef(null)

  // Use selectedValues if provided, otherwise use value
  const currentValue = selectedValues.length > 0 ? selectedValues : value

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
        setSearchTerm("")
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Filter options based on search term
  const filteredOptions = options.filter(option => {
    const label = option[displayKey]
    return label && label.toString().toLowerCase().includes(searchTerm.toLowerCase())
  })

  // Handle option toggle
  const handleOptionToggle = (optionValue) => {
    const newValue = currentValue.includes(optionValue)
      ? currentValue.filter(v => v !== optionValue)
      : [...currentValue, optionValue]
    onChange(newValue)
  }

  // Handle clear all
  const handleClearAll = () => {
    onChange([])
    setSearchTerm("")
  }

  // Get selected options
  const selectedOptions = options.filter(option => currentValue.includes(option[valueKey]))

  return (
    <div className={`position-relative ${className}`} ref={dropdownRef} style={style}>
      {/* Dropdown Button */}
      <Button
        variant="outline-secondary"
        className="w-100 d-flex justify-content-between align-items-center"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          fontSize: "0.7rem",
          minHeight: "38px",
          backgroundColor: "#f9f9fa",
          border: "1px solid #dee2e6",
          borderRadius: "0.375rem",
          padding: "0.375rem 0.75rem"
        }}
      >
        <span className="text-truncate">
          {currentValue.length === 0 
            ? placeholder 
            : `${currentValue.length} selected`
          }
        </span>
        <div className="d-flex align-items-center gap-1">
          {isClearable && currentValue.length > 0 && (
            <i 
              className="fas fa-times text-muted" 
              style={{ fontSize: "0.6rem" }}
              onClick={(e) => {
                e.stopPropagation()
                handleClearAll()
              }}
              title="Clear all"
            />
          )}
          <i 
            className={`fas fa-chevron-${isOpen ? 'up' : 'down'} text-muted`}
            style={{ fontSize: "0.6rem" }}
          />
        </div>
      </Button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="position-absolute w-100 bg-white border rounded shadow-lg"
          style={{
            top: openUpward ? "auto" : "100%",
            bottom: openUpward ? "100%" : "auto",
            left: 0,
            zIndex: 10000,
            maxHeight: maxHeight,
            overflowY: "auto",
            marginTop: openUpward ? "0" : "2px",
            marginBottom: openUpward ? "2px" : "0"
          }}
        >
          {/* Search Input */}
          {isSearchable && (
            <div className="p-2 border-bottom">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ fontSize: "0.7rem" }}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}

          {/* Options List */}
          <div className="py-1">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-muted text-center" style={{ fontSize: "0.7rem" }}>
                {searchTerm ? "No matching options" : "No options available"}
              </div>
            ) : (
              filteredOptions.map((option) => {
                const optionValue = option[valueKey]
                const optionLabel = option[displayKey]
                const isSelected = currentValue.includes(optionValue)
                return (
                  <div
                    key={optionValue}
                    className={`px-3 py-2 d-flex justify-content-between align-items-center cursor-pointer ${
                      isSelected ? 'bg-primary text-white' : 'hover-bg-light'
                    }`}
                    style={{
                      fontSize: "0.7rem",
                      cursor: "pointer",
                      transition: "background-color 0.15s ease"
                    }}
                    onClick={() => handleOptionToggle(optionValue)}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.target.style.backgroundColor = "#f8f9fa"
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.target.style.backgroundColor = "transparent"
                      }
                    }}
                  >
                    <span className="text-truncate">{optionLabel}</span>
                    {isSelected && (
                      <i className="fas fa-check" style={{ fontSize: "0.6rem" }} />
                    )}
                  </div>
                )
              })
            )}
          </div>

          {/* Footer Actions */}
          {currentValue.length > 0 && (
            <div className="p-2 border-top bg-light">
              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted" style={{ fontSize: "0.6rem" }}>
                  {currentValue.length} selected
                </small>
                {isClearable && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={handleClearAll}
                    style={{ fontSize: "0.6rem", padding: "0.25rem 0.5rem" }}
                  >
                    Clear All
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default MultiSelectDropdown
